/**
 * Plan 04-03 — Panel inline que aparece tras tocar "No" en FeedbackWidget (20-oct: también el
 * motivo de "No se vendió").
 *
 * 5 categorías como segmented chips + textarea opcional 500 chars + Enviar/Cancelar.
 * D-UI-FB-01 + UI-SPEC §Feedback panel. Tap targets ≥44px (kiosko tablet).
 *
 * Drimian P9 (medir para entender, no para castigar): el panel NO es modal
 * bloqueante. La farmacéutica puede cerrar con Cancelar y seguir; el chat sigue
 * vivo detrás. NO bloquea Nueva consulta (D-UI-FB-02).
 */
import { useState } from 'react'
import type { FeedbackCategory } from '../types/feedback'
import { FEEDBACK_CATEGORY_LABELS } from '../types/feedback'

interface Props {
  onSubmit: (category: FeedbackCategory, comment: string | undefined) => void
  onCancel: () => void
  categorias?: FeedbackCategory[]
  titulo?: string
}

const CATEGORIES: FeedbackCategory[] = [
  'correct',
  'partial',
  'wrong_dose',
  'wrong_pa',
  'other',
]

const MAX_COMMENT = 500

export function FeedbackPanel({ onSubmit, onCancel, categorias = CATEGORIES, titulo = '¿Qué falló?' }: Props) {
  const [category, setCategory] = useState<FeedbackCategory | null>(null)
  const [comment, setComment] = useState('')

  const handleSubmit = () => {
    if (!category) return
    onSubmit(category, comment.trim() || undefined)
  }

  return (
    <div
      role="group"
      aria-label="Categorizar feedback"
      className="flex flex-col gap-3 mt-2"
      style={{
        padding: '12px',
        borderRadius: 'var(--radius-m)',
        background: 'var(--color-surface-2)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        className="text-[12px] font-medium"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {titulo}
      </div>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {categorias.map((c) => {
          const active = c === category
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setCategory(c)}
              className="text-[13px] transition-colors"
              style={{
                minHeight: 44,
                padding: '8px 14px',
                borderRadius: 'var(--radius-s)',
                border: `1px solid ${active ? 'var(--color-brand)' : 'var(--color-border-strong)'}`,
                background: active
                  ? 'var(--color-brand-soft)'
                  : 'var(--color-surface)',
                color: active
                  ? 'var(--color-brand-ink)'
                  : 'var(--color-text)',
                cursor: 'pointer',
              }}
            >
              {FEEDBACK_CATEGORY_LABELS[c]}
            </button>
          )
        })}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
        placeholder="Comentario (opcional)"
        rows={3}
        maxLength={MAX_COMMENT}
        aria-label="Comentario opcional"
        className="text-[14px] w-full"
        style={{
          padding: '10px',
          borderRadius: 'var(--radius-s)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
          resize: 'vertical',
          color: 'var(--color-text)',
          fontFamily: 'var(--font-sans)',
        }}
      />
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px]"
          style={{
            minHeight: 44,
            padding: '10px 16px',
            borderRadius: 999,
            border: '1px solid var(--color-border-strong)',
            background: 'transparent',
            color: 'var(--color-text)',
            cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!category}
          className="text-[13px] font-medium disabled:opacity-40"
          style={{
            minHeight: 44,
            padding: '10px 16px',
            borderRadius: 999,
            background: 'var(--color-brand)',
            color: 'white',
            border: 'none',
            cursor: category ? 'pointer' : 'not-allowed',
          }}
        >
          Enviar
        </button>
      </div>
    </div>
  )
}
